import React, { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  ScatterChart,
  Scatter,
  LineChart,
} from "recharts";
import Select from "react-select";
import { formatValue } from "../../utils/formatNumber";
import { getChartColors, getThemeColors, Theme } from "../../theme/colors";

type DataItem = Record<string, string | number>;

interface GenericChartProps {
  data: DataItem[];
  theme: Theme;
  username?: string;
}

// Dynamically categorize keys from data
const categorizeKeys = (data: DataItem[]) => {
  if (data.length === 0) return { dimensions: [], measures: [] };
  const firstItem = data[0];
  const dimensions: string[] = [];
  const measures: string[] = [];

  Object.keys(firstItem).forEach((key) => {
    if (typeof firstItem[key] === "number") {
      if (
        (key.toLowerCase().includes("year") &&
          firstItem[key] > 2000 &&
          firstItem[key] < 2030) ||
        (key.includes("month") &&
          firstItem[key] >= 1 &&
          firstItem[key] <= 12) ||
        (key.includes("week") && firstItem[key] >= 1 && firstItem[key] <= 52)
      ) {
        dimensions.push(key);
      } else {
        measures.push(key);
      }
    } else {
      dimensions.push(key);
    }
  });
  return { dimensions, measures };
};

const GenericChart: React.FC<GenericChartProps> = ({ theme, data, username = 'default' }) => {
  const keys = useMemo(() => categorizeKeys(data), [data]);

  // Use multi-select for dimensions; default to first dimension if available.
  const [xAxisKeys, setXAxisKeys] = useState<string[]>(
    keys.dimensions.length > 0 ? [keys.dimensions[0]] : []
  );
  useEffect(() => {
    const valid = keys.dimensions.filter((dim) => xAxisKeys.includes(dim));
    if (valid.length === 0 && keys.dimensions.length > 0) {
      setXAxisKeys([keys.dimensions[0]]);
    } else if (valid.length !== xAxisKeys.length) {
      setXAxisKeys(valid);
    }
  }, [keys, xAxisKeys]);

  // Chart type and measure selections
  const [chartType, setChartType] = useState<
    "bar" | "pie" | "combined" | "scatter" | "line"
  >("bar");
  const [barKeys, setBarKeys] = useState<string[]>(() => {
    return keys.measures.length > 1 ? [keys.measures[1]] : [];
  });
  const [selectedKeys, setSelectedKeys] = useState<string[]>(() => {
    return keys.measures.length > 0 ? [keys.measures[0]] : [];
  });
  const [yAxisWidth, setYAxisWidth] = useState<number>(60);

  // Pagination and Sorting states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortKey, setSortKey] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [pageSize, setPageSize] = useState<number | "all">(10);

  useEffect(() => {
    const updateYAxisWidth = () => {
      const yAxis = document.getElementsByClassName(
        "recharts-cartesian-axis recharts-yAxis"
      )[0];
      if (yAxis) {
        const yAxisWidth = (yAxis as SVGGraphicsElement)?.getBBox().width;
        setYAxisWidth(yAxisWidth);
      }
    };
    setTimeout(updateYAxisWidth, 0);
    window.addEventListener("resize", updateYAxisWidth);
    return () => window.removeEventListener("resize", updateYAxisWidth);
  }, [data]);

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [data, sortKey, sortOrder]);

  // Pagination: if pageSize is "all", show full dataset
  const paginatedData = useMemo(() => {
    if (pageSize === "all") return sortedData;
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  // Create a composite field that concatenates selected dimension values.
  const compositeData = useMemo(() => {
    if (xAxisKeys.length === 0) return paginatedData;
    return paginatedData.map((item) => ({
      ...item,
      compositeDimension: xAxisKeys.map((key) => item[key]).join(" - "),
    }));
  }, [paginatedData, xAxisKeys]);

  // Dropdown options
  const colorTheme = getThemeColors(username, theme);
  const CHART_COLORS = getChartColors(username, theme).desktop;

  const typeOptions = [
    { value: "bar", label: "Bar Chart" },
    { value: "line", label: "Line Chart" },
    { value: "combined", label: "Combined Bar and Line Chart" },
    { value: "scatter", label: "Scatter Plot" },
  ];

  const dimensionOptions = keys.dimensions.map((key) => ({
    value: key,
    label: key.charAt(0).toUpperCase() + key.slice(1),
  }));

  const measureOptions = keys.measures.map((key) => ({
    value: key,
    label: key,
  }));

  const barKeyOptions = keys.measures.map((key) => ({
    value: key,
    label: key,
  }));

  // Combine options for sorting (dimensions + measures)
  const sortOptions = [...dimensionOptions, ...measureOptions];

  // Options for page size selection
  const pageSizeOptions = [
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 15, label: "15" },
    { value: 20, label: "20" },
    { value: "all", label: "All" },
  ];

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      backgroundColor: colorTheme.background,
      color: colorTheme.text,
      borderColor: colorTheme.border,
    }),
    singleValue: (provided: any) => ({
      ...provided,
      backgroundColor: colorTheme.background,
      color: colorTheme.text,
    }),
    option: (provided: any, state: { isSelected: any }) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? colorTheme.multiValueBg
        : colorTheme.background,
      color: colorTheme.text,
      ":hover": {
        backgroundColor: colorTheme.optionHoverBg,
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: colorTheme.background,
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: colorTheme.multiValueBg,
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: colorTheme.text,
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: colorTheme.text,
      ":hover": {
        backgroundColor: colorTheme.removeValueBg,
        color: colorTheme.text,
      },
    }),
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;
  
    const sortOptionsValue = dimensionOptions.map((option) => payload[0].payload[option.value]).join(" - ");

    return (
      <div
        style={{
          backgroundColor: colorTheme.background,
          borderColor: colorTheme.border,
          color: colorTheme.text,
          padding: "10px",
          borderRadius: "5px",
          opacity: 0.9,
        }}
      >
        <p>
          <strong>{sortOptionsValue}</strong>
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index}>
            {entry.name}: {formatValue(entry.value)}
          </p>
        ))}
      </div>
    );
  };
  
  // For non-scatter charts, use compositeData and "compositeDimension" for the X-Axis.
  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={compositeData}>
        <XAxis
          dataKey="compositeDimension"
          tickFormatter={(value: string | number) => formatValue(value, true)}
        />
        <YAxis
          tickFormatter={(value: string | number) => formatValue(value, true)}
          width={yAxisWidth}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: colorTheme.background,
            borderColor: colorTheme.border,
            color: colorTheme.text,
            opacity: 0.9,
          }}
          cursor={false}
          formatter={(value: string | number) => formatValue(value)}
        />
        <Legend />
        {selectedKeys
          .filter((key) => !xAxisKeys.includes(key))
          .map((key, index) => (
            <Bar key={key} dataKey={key} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
      </BarChart>
    </ResponsiveContainer>
  );

  const renderLineChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={compositeData}>
        <XAxis
          dataKey="compositeDimension"
          tickFormatter={(value: string | number) => formatValue(value, true)}
        />
        <YAxis
          tickFormatter={(value: string | number) => formatValue(value, true)}
          width={yAxisWidth}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: colorTheme.background,
            borderColor: colorTheme.border,
            color: colorTheme.text,
            opacity: 0.9,
          }}
          cursor={false}
          formatter={(value: string | number) => formatValue(value)}
        />
        <Legend />
        {selectedKeys
          .filter((key) => !xAxisKeys.includes(key))
          .map((key, index) => (
            <Line
              key={key}
              dataKey={key}
              type="monotone"
              dot={false}
              stroke={CHART_COLORS[index % CHART_COLORS.length]}
              strokeWidth={2}
            />
          ))}
      </LineChart>
    </ResponsiveContainer>
  );

  const renderPieChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <PieChart>
        {selectedKeys.map((key, index) => (
          <Pie
            key={key}
            data={compositeData}
            dataKey={key}
            nameKey="compositeDimension"
            cx="50%"
            cy="50%"
            outerRadius={50 + index * 20}
            fill={CHART_COLORS[index % CHART_COLORS.length]}
            label
          >
            {compositeData.map((entry, idx) => (
              <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
            ))}
          </Pie>
        ))}
        <Tooltip formatter={(value: string | number) => formatValue(value)} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderCombinedChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart data={compositeData}>
        <XAxis
          dataKey="compositeDimension"
          tickFormatter={(value: string | number) => formatValue(value, true)}
        />
        <YAxis
          yAxisId="left"
          tickFormatter={(value: string | number) => formatValue(value, true)}
          width={yAxisWidth}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tickFormatter={(value: string | number) => formatValue(value, true)}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: colorTheme.background,
            borderColor: colorTheme.border,
            color: colorTheme.text,
            opacity: 0.9,
          }}
          cursor={false}
          formatter={(value: string | number) => formatValue(value)}
        />
        <Legend />
        {barKeys.map((key, index) => (
          <Bar
            yAxisId="left"
            key={key}
            dataKey={key}
            fill={CHART_COLORS[index % CHART_COLORS.length]}
          />
        ))}
        {selectedKeys
          .filter((key) => !barKeys.includes(key))
          .map((key, index) => (
            <Line
              key={key}
              yAxisId="right"
              type="monotone"
              dot={false}
              dataKey={key}
              strokeWidth={2}
              stroke={CHART_COLORS[(index + barKeys.length) % CHART_COLORS.length]}
            />
          ))}
      </ComposedChart>
    </ResponsiveContainer>
  );

  // Scatter chart remains unchanged.
  const renderScatterChart = () => {
    const transformedData = paginatedData.map((item) => ({
      ...item,
      [`${barKeys[0]}-${selectedKeys[0]}`]: {
        x: item[barKeys[0]],
        y: item[selectedKeys[0]],
      },
    }));
    return (
      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <XAxis
            type="number"
            dataKey={barKeys[0]}
            name={barKeys[0]}
            label={{ value: barKeys[0], position: "bottom" }}
            tickFormatter={(value: string | number) => formatValue(value, true)}
          />
          <YAxis
            type="number"
            dataKey={selectedKeys[0]}
            name={selectedKeys[0]}
            tickFormatter={(value: string | number) => formatValue(value, true)}
            width={yAxisWidth}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: "3 3" }} />
          <Legend wrapperStyle={{ position: "relative", bottom: "10px" }} />
          {selectedKeys
            .filter((key) => key !== xAxisKeys[0] && !barKeys.includes(key))
            .map((yKey, index) => (
              <Scatter
                key={yKey}
                name={yKey}
                data={transformedData}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
                dataKey={yKey}
              />
            ))}
        </ScatterChart>
      </ResponsiveContainer>
    );
  };

  const renderChart = () => {
    switch (chartType) {
      case "bar":
        return renderBarChart();
      case "line":
        return renderLineChart();
      case "pie":
        return renderPieChart();
      case "combined":
        return renderCombinedChart();
      case "scatter":
        return renderScatterChart();
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Chart Type Selector */}
      <div style={{ display: "flex", alignItems: "flex-start", marginTop: "20px", gap: "10px" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>Type:</span>
          <Select
            options={typeOptions}
            value={typeOptions.find((option) => option.value === chartType)}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setChartType(
                  selectedOption.value as "bar" | "pie" | "combined" | "scatter" | "line"
                );
              }
            }}
            styles={{
              ...customStyles,
              control: (provided) => ({
                ...provided,
                ...customStyles.control,
                width: "280px",
                background: colorTheme.background,
                marginTop: "10px",
              }),
            }}
          />
        </div>
      </div>

      {/* Dimension & Measure Selectors */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "20px", marginTop: "10px", flexWrap: "wrap" }}>
        {chartType !== "scatter" && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>Dimensions:</span>
            <Select
              isMulti
              options={dimensionOptions}
              value={dimensionOptions.filter((option) => xAxisKeys.includes(option.value))}
              onChange={(selectedOptions) => {
                const values = selectedOptions.map((option) => option.value);
                setXAxisKeys(values);
              }}
              styles={{
                ...customStyles,
                control: (provided, state) => ({
                  ...provided,
                  ...customStyles.control,
                  width: "280px",
                  marginTop: "10px",
                  background: colorTheme.background,
                  borderColor: state.isFocused
                    ? colorTheme.background
                    : colorTheme.border,
                }),
              }}
              placeholder="Select dimensions..."
            />
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column" }}>
          {(chartType === "bar" || chartType === "pie" || chartType === "line") && (
            <>
              <span>Measures:</span>
              <Select
                isMulti
                options={measureOptions}
                value={measureOptions.filter((option) => selectedKeys.includes(option.value))}
                onChange={(selectedOptions) => {
                  const values = selectedOptions.map((option) => option.value);
                  setSelectedKeys(values);
                }}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                styles={{
                  ...customStyles,
                  control: (provided) => ({
                    ...provided,
                    ...customStyles.control,
                    width: "400px",
                    background: colorTheme.background,
                    marginTop: "10px",
                  }),
                }}
                placeholder="Select measures..."
              />
            </>
          )}

          {chartType === "scatter" && (
            <>
              <div>
                <span>Measures (X axis):</span>
                <Select
                  isMulti
                  options={barKeyOptions}
                  value={barKeyOptions.filter((option) => barKeys.includes(option.value))}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions.map((option) => option.value);
                    setBarKeys(values);
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  styles={{
                    ...customStyles,
                    control: (provided) => ({
                      ...provided,
                      ...customStyles.control,
                      width: "400px",
                      marginTop: "10px",
                      background: colorTheme.background,
                    }),
                  }}
                  placeholder="Select measures..."
                />
              </div>
              <div style={{ marginTop: "10px" }}>
                <span>Measures (Y axis):</span>
                <Select
                  isMulti
                  options={measureOptions}
                  value={measureOptions.filter((option) => selectedKeys.includes(option.value))}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions.map((option) => option.value);
                    setSelectedKeys(values);
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  styles={{
                    ...customStyles,
                    control: (provided) => ({
                      ...provided,
                      ...customStyles.control,
                      width: "400px",
                      background: colorTheme.background,
                      marginTop: "10px",
                    }),
                  }}
                  placeholder="Select measures..."
                />
              </div>
            </>
          )}

          {chartType === "combined" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
              <div>
                <span>Measures (Left axis):</span>
                <Select
                  isMulti
                  options={barKeyOptions}
                  value={barKeyOptions.filter((option) => barKeys.includes(option.value))}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions.map((option) => option.value);
                    setBarKeys(values);
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  styles={{
                    ...customStyles,
                    control: (provided) => ({
                      ...provided,
                      ...customStyles.control,
                      width: "400px",
                      marginTop: "10px",
                      background: colorTheme.background,
                    }),
                  }}
                  placeholder="Select measures..."
                />
              </div>
              <div>
                <span>Measures (Right axis):</span>
                <Select
                  isMulti
                  options={measureOptions}
                  value={measureOptions.filter((option) => selectedKeys.includes(option.value))}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions.map((option) => option.value);
                    setSelectedKeys(values);
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  styles={{
                    ...customStyles,
                    control: (provided) => ({
                      ...provided,
                      ...customStyles.control,
                      width: "400px",
                      background: colorTheme.background,
                      marginTop: "10px",
                    }),
                  }}
                  placeholder="Select 2nd measures..."
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sorting & Pagination Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginTop: "10px",
          gap: "20px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>Sort By:</span>
          <Select
            options={sortOptions}
            value={sortKey ? sortOptions.find((option) => option.value === sortKey) : null}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setSortKey(selectedOption.value);
                setCurrentPage(1);
              }
            }}
            styles={{
              ...customStyles,
              control: (provided) => ({
                ...provided,
                ...customStyles.control,
                width: "280px",
                background: colorTheme.background,
                marginTop: "10px",
              }),
            }}
            placeholder="Select field to sort by..."
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>Sort Order:</span>
          <Select
            options={[
              { value: "asc", label: "Ascending" },
              { value: "desc", label: "Descending" },
            ]}
            value={sortOrder ? {
              value: sortOrder,
              label: sortOrder === "asc" ? "Ascending" : "Descending",
            } : null}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setSortOrder(selectedOption.value);
                setCurrentPage(1);
              }
            }}
            styles={{
              ...customStyles,
              control: (provided) => ({
                ...provided,
                ...customStyles.control,
                width: "190px",
                background: colorTheme.background,
                marginTop: "10px",
              }),
            }}
            placeholder="Select order by..."
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>Items per page:</span>
          <Select
            options={pageSizeOptions}
            value={
              pageSizeOptions.find((option) => option.value === pageSize) || null
            }
            onChange={(selectedOption) => {
              if (selectedOption) {
                setPageSize(selectedOption.value as number | "all");
                setCurrentPage(1);
              }
            }}
            styles={{
              ...customStyles,
              control: (provided) => ({
                ...provided,
                ...customStyles.control,
                width: "190px",
                background: colorTheme.background,
                marginTop: "10px",
              }),
            }}
          />
        </div>
      </div>

      {/* Render the Chart */}
      <div style={{ marginTop: "20px" }}>{renderChart()}</div>

      {/* Pagination Buttons */}
      {pageSize !== "all" && (
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={() =>
              setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev))
            }
            disabled={currentPage === 1}
            style={{ padding: "5px 10px" }}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of{" "}
            {Math.ceil(sortedData.length / (pageSize as number)) || 1}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) =>
                prev < Math.ceil(sortedData.length / (pageSize as number))
                  ? prev + 1
                  : prev
              )
            }
            disabled={
              currentPage === Math.ceil(sortedData.length / (pageSize as number))
            }
            style={{ padding: "5px 10px" }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default GenericChart;
